import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { ViewEventData } from '@databases/analytics/src/types';
import { useDialog } from '@databases/dialog';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { useEffect, useRef } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages as viewSettingsMessages } from '../i18n';

import useView from './useView';
import useViewPanel from './useViewPanel';

const useDeleteView = (contentId: string) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const { addFlag } = useFlags();

  const { closeViewSettingsPanel } = useViewPanel();
  const { view, setViewId, resolvedView } = useView();
  const { showDialog } = useDialog();

  const intl = useIntl();
  const { formatMessage } = intl;

  const viewCount = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.views.length;
  });

  const viewCountRef = useRef(viewCount);

  useEffect(() => {
    viewCountRef.current = viewCount;
  }, [viewCount]);

  return useCallback(
    (viewId: string, source: ViewEventData['source'], onCloseAdditional?: () => void) => {
      showDialog('ConfirmDialog', {
        heading: formatMessage(messages['toolbar-views.delete-view-heading']),
        body: <p>{formatMessage(messages['toolbar-views.delete-view-text'])}</p>,
        yesLabel: formatMessage(
          viewSettingsMessages['view-actions.delete-view-delete-button']
        ),
        noLabel: formatMessage(messages['toolbar-views.delete-view-cancel-button']),
        appearance: 'danger',
        onClose: (result) => {
          const currentViewCount = viewCountRef.current;

          if (currentViewCount < 2) {
            addFlag({
              title: formatMessage(
                messages['toolbar-views.delete-view-failure-flag-title']
              ),
              description: formatMessage(
                messages['toolbar-views.delete-view-failure-flag-description']
              ),
              icon: () => (
                <ErrorIcon
                  spacing="spacious"
                  label=""
                  color={token('color.text.danger')}
                />
              ),
            });
            return;
          }

          // Focus the menu trigger button after dialog closes
          setTimeout(() => {
            const triggerButton = document.querySelector(
              '.view-actions-menu-trigger-button'
            );
            if (triggerButton instanceof HTMLElement) {
              triggerButton.focus();
            }
          }, 0);

          if (result) {
            void client.sendViewDeletedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              source: source,
              viewId: viewId,
            });

            setViewId(resolvedView.id);
            if (view.id === viewId) {
              closeViewSettingsPanel();
            }
            dispatch(
              addChangeToDraft(contentId, 'main', {
                type: DatabaseChangeType.DELETE_SAVED_VIEW,
                viewId: viewId,
              })
            );

            onCloseAdditional && onCloseAdditional();
          }
        },
      });
    },
    [
      showDialog,
      formatMessage,
      addFlag,
      client,
      contentId,
      setViewId,
      resolvedView.id,
      view.id,
      dispatch,
      closeViewSettingsPanel,
    ]
  );
};

export default useDeleteView;
